import clsx from 'clsx'
import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react'

type RenderTagProps = {
    index: number
    isActive: boolean
    onRemove: () => void
}

type Props = {
    value: string[]
    onChange: (tags: string[]) => void
    placeholder?: string
    disabled?: boolean
    maxTagLength?: number
    renderTag?: (tag: string, helpers: RenderTagProps) => ReactNode
    validValues?: readonly string[]
    allowCustomValues?: boolean
    renderOption?: (
        option: string,
        meta: { isActive: boolean; label: string },
    ) => ReactNode
    getOptionSearchText?: (option: string) => string | string[]
}

type DropdownDirection = 'down' | 'up'

export default function TagsInput({
    value,
    onChange,
    placeholder,
    disabled = false,
    maxTagLength,
    renderTag,
    validValues,
    allowCustomValues,
    renderOption,
    getOptionSearchText,
}: Props): React.JSX.Element {
    const [inputValue, setInputValue] = useState('')
    const [activeTagIndex, setActiveTagIndex] = useState<number | null>(null)
    const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(
        null,
    )
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [dropdownDirection, setDropdownDirection] =
        useState<DropdownDirection>('down')
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState<
        number | undefined
    >(undefined)
    const listboxId = useId()
    const dropdownId = `${listboxId}-dropdown`
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const trimmedInput = inputValue.trim()

    const validValueMap = useMemo(() => {
        if (!validValues || validValues.length === 0) return null

        const map = new Map<string, string>()
        for (const option of validValues) {
            const cleaned = option.trim()
            if (!cleaned) continue

            const normalized = cleaned.toLowerCase()
            if (!map.has(normalized)) {
                map.set(normalized, cleaned)
            }
        }

        return map
    }, [validValues])

    const allowCustomValuesResolved =
        allowCustomValues ?? validValueMap === null

    const selectedTagsSet = useMemo(
        () => new Set(value.map((tag) => tag.toLowerCase())),
        [value],
    )

    const availableOptions = useMemo(() => {
        if (!validValueMap) return []

        const options: string[] = []
        for (const [normalized, original] of validValueMap.entries()) {
            if (!selectedTagsSet.has(normalized)) {
                options.push(original)
            }
        }
        return options
    }, [selectedTagsSet, validValueMap])

    const filteredOptions = useMemo(() => {
        if (!validValueMap) return []

        if (!trimmedInput) {
            return availableOptions
        }

        const query = trimmedInput.toLowerCase()
        return availableOptions.filter((option) => {
            const baseText = option.toLowerCase()
            if (baseText.includes(query)) {
                return true
            }

            if (!getOptionSearchText) {
                return false
            }

            const extraTexts = getOptionSearchText(option)
            if (!extraTexts) return false

            const searchTexts = Array.isArray(extraTexts)
                ? extraTexts
                : [extraTexts]

            return searchTexts.some((text) =>
                text?.toLowerCase().includes(query),
            )
        })
    }, [availableOptions, getOptionSearchText, trimmedInput, validValueMap])

    const dropdownOptions = useMemo(() => {
        if (validValueMap) {
            return filteredOptions
        }

        if (trimmedInput) {
            return [trimmedInput]
        }

        return []
    }, [filteredOptions, trimmedInput, validValueMap])

    const showDropdown =
        isInputFocused && dropdownOptions.length > 0 && !disabled

    useEffect(() => {
        if (!showDropdown) {
            setActiveOptionIndex(null)
            return
        }

        setActiveOptionIndex((prev) => {
            if (prev === null || prev >= dropdownOptions.length) {
                return dropdownOptions.length > 0 ? 0 : null
            }
            return prev
        })
    }, [dropdownOptions, showDropdown])

    useEffect(() => {
        if (disabled) {
            setActiveTagIndex(null)
            setActiveOptionIndex(null)
        }
    }, [disabled])

    useEffect(() => {
        if (activeTagIndex !== null && activeTagIndex >= value.length) {
            setActiveTagIndex(value.length > 0 ? value.length - 1 : null)
        }
    }, [activeTagIndex, value.length])

    const getScrollableParent = useCallback(
        (element: HTMLElement | null): HTMLElement | null => {
            let current: HTMLElement | null = element?.parentElement ?? null
            while (current) {
                const style = window.getComputedStyle(current)
                const overflowY = style.overflowY
                const canScrollY =
                    (overflowY === 'auto' || overflowY === 'scroll') &&
                    current.scrollHeight > current.clientHeight
                if (canScrollY) return current
                current = current.parentElement
            }
            return null
        },
        [],
    )

    const updateDropdownPosition = useCallback(() => {
        if (!showDropdown) return

        const containerEl = containerRef.current
        const dropdownEl = dropdownRef.current
        if (!containerEl || !dropdownEl) return

        const scrollParent = getScrollableParent(containerEl)
        const bounds = scrollParent
            ? scrollParent.getBoundingClientRect()
            : { top: 0, bottom: window.innerHeight }

        const containerRect = containerEl.getBoundingClientRect()
        const dropdownHeight = dropdownEl.offsetHeight
        const spaceBelow = bounds.bottom - containerRect.bottom
        const spaceAbove = containerRect.top - bounds.top

        const shouldDropUp =
            dropdownHeight > spaceBelow && spaceAbove > spaceBelow

        const nextDirection: DropdownDirection = shouldDropUp ? 'up' : 'down'
        setDropdownDirection(nextDirection)

        const availableSpace =
            nextDirection === 'down' ? spaceBelow : spaceAbove
        const adjustedMaxHeight = Math.min(Math.max(availableSpace - 8, 0), 224)
        setDropdownMaxHeight(
            adjustedMaxHeight > 0 ? adjustedMaxHeight : undefined,
        )
    }, [getScrollableParent, showDropdown])

    useLayoutEffect(() => {
        if (!showDropdown) {
            setDropdownMaxHeight(undefined)
            return
        }
        updateDropdownPosition()
    }, [
        showDropdown,
        dropdownOptions.length,
        trimmedInput,
        updateDropdownPosition,
    ])

    useEffect(() => {
        if (!showDropdown) return

        const handleWindowChange = () => updateDropdownPosition()

        window.addEventListener('resize', handleWindowChange)
        window.addEventListener('scroll', handleWindowChange, true)

        return () => {
            window.removeEventListener('resize', handleWindowChange)
            window.removeEventListener('scroll', handleWindowChange, true)
        }
    }, [showDropdown, updateDropdownPosition])

    const addValueToList = (
        existingTags: string[],
        rawValue: string,
    ): string[] | null => {
        const trimmed = rawValue.trim()
        if (!trimmed) return null

        const normalized = trimmed.toLowerCase()
        const matchingValidValue = validValueMap?.get(normalized)

        let resolvedValue: string | null = null
        if (matchingValidValue) {
            if (!maxTagLength || matchingValidValue.length <= maxTagLength) {
                resolvedValue = matchingValidValue
            }
        } else if (validValueMap && !allowCustomValuesResolved) {
            resolvedValue = null
        } else if (!maxTagLength || trimmed.length <= maxTagLength) {
            resolvedValue = trimmed
        }

        if (!resolvedValue) {
            return null
        }

        const hasDuplicate = existingTags.some(
            (tag) => tag.toLowerCase() === resolvedValue.toLowerCase(),
        )
        if (hasDuplicate) {
            return existingTags
        }

        return [...existingTags, resolvedValue]
    }

    const commitValue = (nextValue?: string) => {
        addTag(nextValue ?? inputValue)
    }

    const addTag = (rawValue: string) => {
        if (disabled) return

        const trimmed = rawValue.trim()
        const nextTags = addValueToList(value, trimmed)

        if (!nextTags) {
            setInputValue(trimmed)
            setActiveTagIndex(null)
            return
        }

        if (nextTags === value) {
            setInputValue('')
            setActiveTagIndex(null)
            return
        }

        onChange(nextTags)
        setInputValue('')
        setActiveTagIndex(null)
        setActiveOptionIndex(null)
    }

    const removeTag = (index: number, keepSelection = false) => {
        if (disabled) return
        if (index < 0 || index >= value.length) return

        const nextTags = value.filter((_, idx) => idx !== index)
        onChange(nextTags)

        if (!keepSelection) {
            setActiveTagIndex(null)
            return
        }

        if (nextTags.length === 0) {
            setActiveTagIndex(null)
            inputRef.current?.focus()
            return
        }

        const nextIndex = Math.min(index, nextTags.length - 1)
        setActiveTagIndex(nextIndex)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return

        if (
            showDropdown &&
            (event.key === 'ArrowDown' || event.key === 'ArrowUp')
        ) {
            event.preventDefault()
            setActiveOptionIndex((prev) => {
                if (dropdownOptions.length === 0) return null
                if (prev === null) {
                    return event.key === 'ArrowDown'
                        ? 0
                        : dropdownOptions.length - 1
                }

                if (event.key === 'ArrowDown') {
                    return Math.min(dropdownOptions.length - 1, prev + 1)
                }

                return Math.max(0, prev - 1)
            })
            setActiveTagIndex(null)
            return
        }

        if (showDropdown && event.key === 'Escape') {
            event.preventDefault()
            setActiveOptionIndex(null)
            return
        }

        if (
            event.key === 'Enter' ||
            event.key === 'NumpadEnter' ||
            event.key === ',' ||
            event.key === 'Comma'
        ) {
            event.preventDefault()
            if (showDropdown && dropdownOptions.length > 0) {
                const selectedOption =
                    activeOptionIndex !== null
                        ? dropdownOptions[activeOptionIndex]
                        : dropdownOptions[0]
                commitValue(selectedOption)
                return
            }

            commitValue()
            return
        }

        if (event.key === 'Backspace' && inputValue.trim() === '') {
            if (value.length === 0) {
                return
            }

            event.preventDefault()
            if (activeTagIndex === null) {
                setActiveTagIndex(value.length - 1)
                return
            }

            removeTag(activeTagIndex, true)
            return
        }

        if (
            event.key === 'ArrowLeft' &&
            inputValue.trim() === '' &&
            value.length > 0
        ) {
            event.preventDefault()
            if (activeTagIndex === null) {
                setActiveTagIndex(value.length - 1)
            } else {
                setActiveTagIndex(Math.max(0, activeTagIndex - 1))
            }
        }

        if (event.key === 'ArrowRight' && inputValue.trim() === '') {
            if (activeTagIndex !== null) {
                event.preventDefault()
                setActiveTagIndex(null)
                inputRef.current?.focus()
                setActiveOptionIndex(null)
            }
        }
    }

    const handleContainerFocus = (event: React.FocusEvent<HTMLDivElement>) => {
        if (
            event.target === containerRef.current &&
            !disabled &&
            event.relatedTarget !== inputRef.current
        ) {
            inputRef.current?.focus()
        }
    }

    const handleContainerClick = () => {
        if (!disabled) {
            inputRef.current?.focus()
        }
    }

    return (
        <div className="relative w-full">
            <div
                ref={containerRef}
                role="listbox"
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onFocus={handleContainerFocus}
                onClick={handleContainerClick}
                className={clsx(
                    'w-full min-h-[44px] rounded-md border border-gray-300 bg-white px-2 py-2',
                    'flex flex-wrap items-center gap-2',
                    'focus-within:border-ctxt-blue focus-within:ring-2 focus-within:ring-ctxt-blue',
                    disabled && 'cursor-not-allowed bg-gray-100 text-gray-400',
                )}
            >
                {value.map((tag, index) => {
                    const tagId = `${listboxId}-tag-${index}`
                    const isActive = index === activeTagIndex
                    const handleRemove = () => {
                        removeTag(index)
                        inputRef.current?.focus()
                    }

                    const tagNode = renderTag?.(tag, {
                        index,
                        isActive,
                        onRemove: handleRemove,
                    }) ?? (
                        <>
                            <span className="block max-w-[220px] truncate">
                                {tag}
                            </span>
                            <button
                                type="button"
                                aria-label={`Remove '${tag}'`}
                                className={clsx(
                                    'flex h-5 w-5 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-200 hover:text-gray-700',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ctxt-blue',
                                    disabled &&
                                        'cursor-not-allowed hover:bg-gray-100',
                                )}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    handleRemove()
                                }}
                                disabled={disabled}
                            >
                                ×
                            </button>
                        </>
                    )

                    return (
                        <div
                            key={tagId}
                            id={tagId}
                            role="option"
                            aria-selected={isActive}
                            className={clsx(
                                'group flex min-w-0 items-center whitespace-nowrap',
                                renderTag
                                    ? null
                                    : 'gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800',
                                isActive &&
                                    'ring-2 ring-ctxt-blue ring-offset-1',
                                disabled && 'opacity-70',
                            )}
                            onClick={() => {
                                if (disabled) return
                                setInputValue(tag)
                                removeTag(index)
                                inputRef.current?.focus()
                            }}
                        >
                            {tagNode}
                        </div>
                    )
                })}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(event) => {
                        setInputValue(event.target.value)
                        setActiveTagIndex(null)
                    }}
                    onBlur={() => {
                        setIsInputFocused(false)
                        commitValue()
                    }}
                    onFocus={() => {
                        setIsInputFocused(true)
                    }}
                    onKeyDown={handleKeyDown}
                    aria-activedescendant={
                        showDropdown && activeOptionIndex !== null
                            ? `${dropdownId}-option-${activeOptionIndex}`
                            : activeTagIndex !== null
                              ? `${listboxId}-tag-${activeTagIndex}`
                              : undefined
                    }
                    aria-controls={showDropdown ? dropdownId : undefined}
                    aria-expanded={showDropdown}
                    aria-label="Add tag"
                    placeholder={value.length === 0 ? placeholder : undefined}
                    disabled={disabled}
                    maxLength={maxTagLength}
                    className={clsx(
                        'flex-1 min-w-[140px] border-none bg-transparent px-1 py-1 text-sm text-gray-900 outline-none',
                        'placeholder:text-gray-400',
                        disabled && 'cursor-not-allowed text-gray-400',
                    )}
                />
            </div>

            {showDropdown && (
                <div
                    id={dropdownId}
                    role="listbox"
                    ref={dropdownRef}
                    className="absolute left-0 right-0 z-10 flex flex-col rounded-md border border-gray-200 bg-white shadow-lg"
                    style={
                        dropdownDirection === 'down'
                            ? {
                                  top: '100%',
                                  marginTop: '4px',
                                  maxHeight: dropdownMaxHeight,
                              }
                            : {
                                  bottom: '100%',
                                  marginBottom: '4px',
                                  maxHeight: dropdownMaxHeight,
                              }
                    }
                >
                    <div className="flex-1 overflow-y-auto">
                        {dropdownOptions.map((option, index) => {
                            const optionId = `${dropdownId}-option-${index}`
                            const isActive = index === activeOptionIndex
                            const label = validValueMap
                                ? option
                                : `Add "${option}"`

                            return (
                                <div
                                    key={optionId}
                                    id={optionId}
                                    role="option"
                                    aria-selected={isActive}
                                    className={clsx(
                                        'flex cursor-pointer items-start gap-1 px-3 py-2 text-sm text-gray-800',
                                        'hover:bg-gray-100',
                                        isActive && 'bg-gray-100',
                                    )}
                                    onMouseDown={(event) =>
                                        event.preventDefault()
                                    }
                                    onClick={() => {
                                        commitValue(option)
                                        inputRef.current?.focus()
                                    }}
                                >
                                    {renderOption ? (
                                        renderOption(option, {
                                            isActive,
                                            label,
                                        })
                                    ) : (
                                        <span className="truncate">
                                            {label}
                                        </span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                    {validValueMap && (
                        <div className="flex items-center justify-end border-t border-gray-200 px-3 py-2">
                            <button
                                type="button"
                                className="text-sm font-semibold text-ctxt-blue hover:text-ctxt-blue/80"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                    let nextTags = value
                                    for (const option of dropdownOptions) {
                                        const updated = addValueToList(
                                            nextTags,
                                            option,
                                        )
                                        if (updated) {
                                            nextTags = updated
                                        }
                                    }
                                    if (nextTags !== value) {
                                        onChange(nextTags)
                                        setInputValue('')
                                        setActiveTagIndex(null)
                                        setActiveOptionIndex(null)
                                    }
                                    inputRef.current?.focus()
                                }}
                            >
                                Add All
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
